<template>
  <div class="buy-cdk-page">
    <!-- 顶栏 -->
    <header class="cdk-topbar">
      <div class="cdk-topbar-inner">
        <div class="cdk-topbar-left">
          <button v-if="canGoBack" type="button" class="cdk-back" title="返回" @click="goBack">
            <i class="el-icon-back"></i>
          </button>
          <div class="cdk-brand">
            <i class="el-icon-key cdk-brand-icon"></i>
            <span class="cdk-brand-text">兑换码</span>
          </div>
        </div>
      </div>
    </header>

    <main class="cdk-body">
      <div v-if="loadingSellers" class="cdk-loading" v-loading="true"></div>

      <div v-else-if="!sellers.length" class="cdk-lock">
        <!-- 未开通：做成宣传页式的引导，让用户有开通欲望 -->
        <section class="cdk-lock-hero">
          <span class="cdk-lock-badge"><i class="el-icon-star-on"></i> 服务商专享</span>
          <h2 class="cdk-lock-title">开通兑换码批发权限</h2>
          <p class="cdk-lock-sub">批量购码 · 专属折扣 · 激活追踪，一站式管理你的兑换码</p>

          <ul class="cdk-lock-benefits">
            <li v-for="item in lockBenefits" :key="item.title">
              <i :class="item.icon"></i>
              <div class="cdk-lock-benefit-text">
                <b>{{ item.title }}</b>
                <span>{{ item.desc }}</span>
              </div>
            </li>
          </ul>

          <button type="button" class="cdk-lock-cta" :disabled="activating" @click="onPrimaryAction">
            <span>{{ activating ? '开通中…' : (queryAppid ? '联系客服开通' : '立即开通兑换码权限') }}</span>
            <i class="el-icon-arrow-right"></i>
          </button>
          <p class="cdk-lock-hint">
            <i class="el-icon-info"></i>
            <span v-if="queryAppid">该软件由专属客服协助开通，点击上方按钮获取客服二维码。</span>
            <span v-else>{{ canActivate ? '你已具备开通资格，点击上方按钮即可直接开通。' : '激活码由管理员发放，联系客服获取后即可自助激活。' }}</span>
          </p>
        </section>

        <!-- 权益预览（示意，不含真实数据） -->
        <section class="cdk-lock-preview">
          <div class="cdk-lock-preview-head">
            <span>开通后你将看到</span>
            <span class="cdk-lock-preview-tag"><i class="el-icon-lock"></i> 未解锁</span>
          </div>
          <div class="cdk-lock-preview-table">
            <div class="cdk-lock-preview-row cdk-lock-preview-row--head">
              <span>兑换码</span><span>套餐</span><span>状态</span><span>激活用户</span>
            </div>
            <div class="cdk-lock-preview-row" v-for="n in 4" :key="n">
              <span class="cdk-lock-bar w1"></span>
              <span class="cdk-lock-bar w2"></span>
              <span class="cdk-lock-bar w3"></span>
              <span class="cdk-lock-bar w4"></span>
            </div>
          </div>
          <p class="cdk-lock-preview-tip">购买记录、激活状态与导出功能，开通后立即可用</p>
        </section>
      </div>

      <template v-else>
        <!-- 购买区 -->
        <section class="cdk-section">
          <div class="cdk-section-head">
            <h3 class="cdk-section-title">购买兑换码</h3>
            <div v-if="sellers.length > 1" class="cdk-seller-switch">
              <span class="cdk-seller-label">适用小程序</span>
              <el-radio-group v-model="currentAppid" size="small" @change="handleSellerChange">
                <el-radio-button v-for="item in sellers" :key="item.appid || item.proxy_name" :label="item.appid">
                  {{ item.app_name }}
                </el-radio-button>
              </el-radio-group>
            </div>
            <span v-else class="cdk-seller-single">
              适用于 <b>{{ currentSeller ? currentSeller.app_name : '' }}</b>
            </span>
            <span v-if="rate < 1" class="cdk-rate-tag">{{ formatDiscount(rate) }}</span>
          </div>

          <div class="cdk-card">
            <div class="cdk-field cdk-field-block">
              <div class="cdk-field-label">选择套餐</div>
              <div class="cdk-package-grid" v-loading="loadingPackages">
                <button v-for="item in packages" :key="item.id" type="button" class="cdk-package"
                  :class="{ active: packageId === item.id }"
                  @click="selectPackage(item.id)">
                  <span class="cdk-package-top">
                    <span class="cdk-package-name">
                      {{ item.name }}
                      <em v-if="item.license" class="cdk-package-badge">代理卡</em>
                    </span>
                    <span class="cdk-package-price">¥{{ item.pay }}</span>
                  </span>
                  <span v-if="rate < 1" class="cdk-package-origin">原价 ¥{{ originPay(item) }}</span>
                  <template v-if="item.license">
                    <span class="cdk-package-meta">发给下级激活，不会自动开通</span>
                    <span class="cdk-package-meta">
                      适用：{{ currentSeller ? currentSeller.app_name : '当前软件' }}
                    </span>
                  </template>
                  <template v-else>
                    <span class="cdk-package-meta">{{ packageMeta(item) }}</span>
                    <span class="cdk-package-meta">每日限制：{{ item.today > 0 ? item.today + ' 次' : '不限' }}</span>
                  </template>
                </button>
                <div v-if="!loadingPackages && !packages.length" class="cdk-nopackage">暂无可购买套餐</div>
              </div>
            </div>

            <div class="cdk-field">
              <div class="cdk-field-label">购买数量</div>
              <el-input-number v-model="num" :min="1" :max="100" size="small" />
            </div>

            <div class="cdk-field">
              <div class="cdk-field-label">备注</div>
              <el-input v-model="remark" clearable maxlength="100" placeholder="请输入本次购买备注" size="small"
                class="cdk-remark-input" />
            </div>

            <div class="cdk-field cdk-field-block">
              <div class="cdk-field-label">支付方式</div>
              <div class="cdk-pay-methods">
                <button type="button" class="cdk-pay-method" :class="{ active: payMethod === 'balance' }"
                  @click="payMethod = 'balance'">
                  <i class="el-icon-wallet"></i>
                  <span class="cdk-pay-text">
                    <span class="cdk-pay-name">余额支付</span>
                    <span class="cdk-pay-desc">余额 ¥{{ formatNumber(balance) }}</span>
                  </span>
                </button>
                <button type="button" class="cdk-pay-method" :class="{ active: payMethod === 'alipay' }"
                  @click="payMethod = 'alipay'">
                  <i class="el-icon-money"></i>
                  <span class="cdk-pay-text">
                    <span class="cdk-pay-name">支付宝扫码</span>
                    <span class="cdk-pay-desc">扫码支付，实时发码</span>
                  </span>
                </button>
              </div>
            </div>

            <div class="cdk-actions">
              <el-button type="primary" size="medium" :loading="buying"
                :disabled="buying || confirming || !selectedPackage" @click="buy">
                {{ purchaseButtonText }}
              </el-button>
              <span class="cdk-actions-tip">
                当前账户余额 <b>¥{{ formatNumber(balance) }}</b>
              </span>
            </div>
          </div>
        </section>

        <!-- 兑换码列表 -->
        <section class="cdk-section">
          <div class="cdk-section-head">
            <h3 class="cdk-section-title">我的兑换码</h3>
            <span class="cdk-section-sub">共 {{ total }} 条</span>
          </div>

          <div class="cdk-card">
            <el-form :inline="true" size="small" class="cdk-filters" @submit.native.prevent>
              <el-form-item label="套餐">
                <el-select v-model="filterVipid" class="cdk-w-pkg" placeholder="全部" @change="searchCdks">
                  <el-option label="全部" :value="''">
                    <span>全部</span>
                    <span class="cdk-option-count">{{ summaryTotal }} 个</span>
                  </el-option>
                  <el-option v-for="item in packageSummary" :key="item.vipid" :label="item.name" :value="item.vipid">
                    <span>{{ item.name }}</span>
                    <span class="cdk-option-count">{{ item.total }} 个 · 未用 {{ item.unused }}</span>
                  </el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="状态">
                <el-select v-model="usageStatus" class="cdk-w-sm" @change="searchCdks">
                  <el-option label="全部" value="all" />
                  <el-option label="未使用" value="unused" />
                  <el-option label="已使用" value="used" />
                </el-select>
              </el-form-item>
              <el-form-item label="日期">
                <el-date-picker v-model="dateRange" type="daterange" value-format="yyyy-MM-dd" range-separator="至"
                  start-placeholder="开始日期" end-placeholder="结束日期" clearable class="cdk-w-date"
                  @change="searchCdks" />
              </el-form-item>
              <el-form-item label="搜索">
                <el-select v-model="searchType" class="cdk-w-sm">
                  <el-option label="兑换码" value="name" />
                  <el-option label="备注" value="remark" />
                  <el-option label="激活用户" value="username" />
                </el-select>
                <el-input v-model="keyword" clearable class="cdk-w-md"
                  :placeholder="searchPlaceholder" prefix-icon="el-icon-search"
                  @keyup.enter.native="searchCdks" />
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="searchCdks">查询</el-button>
                <el-button @click="resetFilters">重置</el-button>
              </el-form-item>
            </el-form>

            <div class="cdk-toolbar cdk-toolbar-actions">
              <el-button type="success" size="small" icon="el-icon-circle-check" :disabled="!selectedRows.length"
                @click="openBatchRedeem">批量兑换</el-button>
              <el-button size="small" icon="el-icon-edit-outline" :disabled="!selectedRows.length"
                @click="openBatchRemark">批量改备注</el-button>
              <el-button size="small" :disabled="!selectedRows.length" @click="copySelected">复制</el-button>
              <el-button size="small" :disabled="!selectedRows.length" @click="exportSelected">导出</el-button>
              <span v-if="selectedRows.length" class="cdk-selected-tip">已选 {{ selectedRows.length }} 条</span>
            </div>

            <el-table :data="records" size="small" v-loading="loading" :row-class-name="usedRowClass"
              @selection-change="handleSelectionChange">
              <el-table-column type="selection" width="46" />
              <el-table-column prop="name" label="卡密（点击可复制）" min-width="230">
                <template slot-scope="scope">
                  <span class="cdk-type-tag" :class="{ 'is-license': scope.row.license }">
                    {{ scope.row.license ? '代理卡' : '兑换码' }}
                  </span>
                  <span class="cdk-link" @click="copyText(scope.row.name)">{{ scope.row.name }}</span>
                </template>
              </el-table-column>
              <el-table-column label="内容" min-width="200">
                <template slot-scope="scope">{{ recordMeta(scope.row) }}</template>
              </el-table-column>
              <el-table-column prop="username" label="激活用户" min-width="110">
                <template slot-scope="scope">{{ scope.row.username || '未激活' }}</template>
              </el-table-column>
              <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip>
                <template slot-scope="scope">{{ scope.row.remark || '—' }}</template>
              </el-table-column>
              <el-table-column label="状态" width="98" align="center">
                <template slot-scope="scope">
                  <span class="cdk-dot" :class="scope.row.status === 1 ? 'on' : 'off'"></span>
                  {{ scope.row.status === 1 ? '未使用' : (scope.row.license ? '已激活' : '已使用') }}
                </template>
              </el-table-column>
              <el-table-column label="日期" width="150">
                <template slot-scope="scope">{{ formatDate(scope.row.intime) }}</template>
              </el-table-column>
            </el-table>

            <div class="cdk-pager">
              <el-pagination small layout="total, prev, pager, next" :page-size="pageSize" :total="total"
                :current-page="page" @current-change="changePage" />
            </div>
          </div>
        </section>
      </template>
    </main>

    <!-- 批量兑换 -->
    <el-dialog title="批量兑换" :visible.sync="batchVisible" width="620px" custom-class="cdk-dialog">
      <el-alert title="用户名支持换行、空格、逗号和分号分割，并按兑换码顺序一一对应；只能兑换给您名下的用户。"
        type="info" :closable="false" show-icon />
      <el-input v-model="batchUsernames" type="textarea" :rows="5" placeholder="请粘贴用户名，每行一个"
        class="cdk-batch-input" />
      <div class="cdk-batch-count" :class="{ error: parsedUsernames.length !== batchRows.length }">
        已选 {{ batchRows.length }} 个兑换码，识别到 {{ parsedUsernames.length }} 个用户名
      </div>
      <div class="cdk-batch-preview">
        <div v-for="(item, index) in batchPreview" :key="item.id" class="cdk-batch-item">
          <b>#{{ index + 1 }} {{ item.name }}</b>
          <span :class="{ error: !item.username }">{{ item.username || '待输入用户名' }}</span>
          <em v-if="item.result" :class="item.result.success ? 'success' : 'error'">{{ item.result.msg }}</em>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="batchVisible = false">关闭</el-button>
        <el-button type="primary" :loading="batchLoading" :disabled="!canSubmitBatch" @click="submitBatch">确认批量兑换</el-button>
      </span>
    </el-dialog>

    <!-- 批量改备注 -->
    <el-dialog title="批量改备注" :visible.sync="remarkVisible" width="460px" custom-class="cdk-dialog">
      <el-input v-model="batchRemark" clearable maxlength="100" placeholder="请输入新的备注内容" />
      <span slot="footer">
        <el-button @click="remarkVisible = false">取消</el-button>
        <el-button type="primary" :loading="remarkLoading" @click="submitBatchRemark">保存</el-button>
      </span>
    </el-dialog>

    <!-- 联系客服开通（URL 带 ?appid= 时引导） -->
    <el-dialog :visible.sync="kfVisible" width="380px" custom-class="cdk-kf-dialog" :show-close="false">
      <div class="cdk-kf">
        <button type="button" class="cdk-kf-close" @click="kfVisible = false">
          <i class="el-icon-close"></i>
        </button>

        <div class="cdk-kf-badge"><i class="el-icon-service"></i></div>
        <h3 class="cdk-kf-title">需要联系客服开通</h3>
        <p class="cdk-kf-desc">兑换码批发权限需由客服协助开通<br>扫码添加客服，说明来意即可</p>

        <div class="cdk-kf-qr" v-loading="kfLoading">
          <el-image v-if="kfUrl" :src="kfUrl" fit="contain" :preview-src-list="[kfUrl]" />
          <div v-else-if="!kfLoading" class="cdk-kf-empty">
            <i class="el-icon-picture-outline"></i>
            <span>该软件暂未配置客服二维码</span>
          </div>
        </div>

        <p class="cdk-kf-hint"><i class="el-icon-info"></i> 点击二维码可放大查看</p>
        <button type="button" class="cdk-kf-btn" @click="kfVisible = false">我知道了</button>
      </div>
    </el-dialog>

    <!-- 支付宝扫码支付 -->
    <el-dialog title="支付宝扫码支付" :visible.sync="qrVisible" width="400px" custom-class="cdk-dialog"
      :close-on-click-modal="false" @closed="stopQrPoll">
      <div class="cdk-qr">
        <div class="cdk-qr-amount">应付 <b>¥{{ formatNumber(qrData.amount) }}</b></div>
        <div class="cdk-qr-sub">{{ qrData.subject }} × {{ qrData.num }}</div>
        <div id="cdk-alipay-qrcode" class="cdk-qr-box" v-loading="qrGenerating"></div>
        <div class="cdk-qr-tip">
          <i class="el-icon-mobile-phone"></i>
          打开支付宝「扫一扫」完成支付，成功后自动发放，无需再操作
        </div>
      </div>
      <span slot="footer">
        <el-button @click="qrVisible = false">取消</el-button>
        <el-button type="primary" :loading="qrChecking" @click="checkQrPaid">我已完成支付</el-button>
      </span>
    </el-dialog>

    <!-- 购买「兑换码代理开通」后展示卡密 -->
    <el-dialog title="代理卡已生成" :visible.sync="codesVisible" width="560px" custom-class="cdk-dialog">
      <el-alert title="卡密不绑定使用者：发给谁，谁在「兑换码」页输入卡密即可开通兑换码权限。"
        type="info" :closable="false" show-icon />
      <div class="cdk-codes-tip">
        适用软件：<b>{{ generatedInfo.app_name || '全部软件通用' }}</b>
        <span class="cdk-codes-sep">·</span>开通后折扣：<b>{{ formatDiscount(generatedInfo.discount) }}</b>
        <span class="cdk-codes-sep">·</span>本次扣款：<b>¥{{ formatNumber(generatedInfo.price) }}</b>
      </div>
      <div class="cdk-codes">
        <div v-for="(item, index) in generatedCodes" :key="item" class="cdk-codes-item">
          <span class="cdk-codes-index">#{{ index + 1 }}</span>
          <span class="cdk-codes-name">{{ item }}</span>
          <button type="button" class="cdk-codes-copy" @click="copyText(item)">复制</button>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="copyAllCodes">复制全部</el-button>
        <el-button type="primary" @click="codesVisible = false">知道了</el-button>
      </span>
    </el-dialog>

    <!-- 激活码开通（自绘弹窗） -->
    <el-dialog :visible.sync="codeVisible" width="440px" custom-class="cdk-code-dialog" :show-close="false"
      :close-on-click-modal="!codeSubmitting">
      <div class="code-modal">
        <button type="button" class="code-modal-close" @click="closeCodeModal">
          <i class="el-icon-close"></i>
        </button>

        <div class="code-modal-badge"><i class="el-icon-key"></i></div>
        <h3 class="code-modal-title">激活兑换码权限</h3>
        <p class="code-modal-desc">请输入管理员发放的激活码<br>激活后即可购买兑换码并查看已发出的记录</p>

        <div class="code-modal-field" :class="{ 'is-error': !!codeError }">
          <input ref="codeInput" v-model.trim="codeValue" class="code-modal-input" type="text"
            placeholder="XXXX-XXXX-XXXX-XXXX" maxlength="64" autocomplete="off" spellcheck="false"
            @keyup.enter="submitCode" />
        </div>
        <p class="code-modal-hint" :class="{ 'is-error': !!codeError }">{{ codeError }}</p>

        <button type="button" class="code-modal-submit" :disabled="codeSubmitting || !codeValue"
          @click="submitCode">
          {{ codeSubmitting ? '激活中…' : '立即激活' }}
        </button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import QRCode from 'qrcodejs2'

export default {
  name: 'BuyCdk',
  data() {
    return {
      loadingSellers: true,
      canGoBack: false,
      sellers: [],
      canActivate: false,
    // 未开通时的权益宣传（都是该页实际提供的能力）
    lockBenefits: [
      { icon: 'el-icon-shopping-cart-full', title: '批量购码与专属折扣', desc: '按套餐批量购买，服务商享折扣率' },
      { icon: 'el-icon-view', title: '激活状态全程可查', desc: '每张码的套餐、状态与激活用户一目了然' },
      { icon: 'el-icon-download', title: '一键导出明细', desc: '导出兑换码、套餐、状态与激活用户，方便对账' },
      { icon: 'el-icon-link', title: '多小程序统一管理', desc: '名下多个小程序共用同一套兑换码后台' }
    ],
      activating: false,
      codeVisible: false,
      codeValue: '',
      codeError: '',
      codeSubmitting: false,
      currentAppid: '',
      packages: [],
      rate: 1,
      balance: 0,
      packageId: '',
      selectedPackage: null,
      num: 1,
      remark: '',
      buying: false,
      confirming: false,
      loadingPackages: false,

      records: [],
      selectedRows: [],
      loading: false,
      page: 1,
      pageSize: 10,
      total: 0,
      searchType: 'name',
      keyword: '',
      usageStatus: 'all',
      dateRange: [],
      filterVipid: '',
      packageSummary: [],
      summaryTotal: 0,

      batchVisible: false,
      batchRows: [],
      batchUsernames: '',
      batchResults: [],
      batchLoading: false,

      remarkVisible: false,
      batchRemark: '',
      remarkLoading: false,

      payMethod: 'balance',
      qrVisible: false,
      qrGenerating: false,
      qrChecking: false,
      qrTimer: null,
      qrData: { ordernum: '', amount: 0, subject: '', num: 1, kind: 'cdk' },
      // URL 里若带 ?appid=<代理 appid>，开通入口改为"联系客服开通"
      queryAppid: '',
      kfVisible: false,
      kfUrl: '',
      kfLoading: false,
      codesVisible: false,
      generatedCodes: [],
      generatedInfo: { app_name: '', discount: 1, unit_price: 0, price: 0 },
    }
  },
  computed: {
    searchPlaceholder() {
      var map = { name: '搜索兑换码', remark: '搜索备注', username: '搜索激活用户' }
      return map[this.searchType] || '搜索兑换码'
    },
    currentSeller() {
      var list = this.sellers || []
      for (var i = 0; i < list.length; i++) {
        if (list[i].appid === this.currentAppid) return list[i]
      }
      return list.length ? list[0] : null
    },
    purchaseButtonText() {
      if (this.selectedPackage) {
        if (this.payMethod === 'alipay') {
          return (this.selectedPackage.pay * this.num).toFixed(2) + ' 元支付宝支付'
        }
        if (this.selectedPackage.license) {
          return (this.selectedPackage.pay * this.num).toFixed(2) + ' 元购买代理卡'
        }
        return (this.selectedPackage.pay * this.num).toFixed(2) + ' 元购买'
      }
      return '立即购买'
    },
    parsedUsernames() {
      return this.batchUsernames.split(/[\s,，;；]+/).map(function (item) { return item.trim() }).filter(Boolean)
    },
    batchPreview() {
      var self = this
      return this.batchRows.map(function (item, index) {
        return {
          id: item.id,
          name: item.name,
          username: self.parsedUsernames[index] || '',
          result: self.batchResults[index] || null,
        }
      })
    },
    canSubmitBatch() {
      return !this.batchLoading && this.batchRows.length > 0
        && this.parsedUsernames.length === this.batchRows.length
    },
  },
  created() {
    // qrcodejs2 实例不放 data（不需要响应式，Vue 递归 observe 反而多余）
    this.qrcode_m = null
    // URL 带 ?appid=<代理 appid> 时，开通入口改成"联系客服开通"并预取客服二维码
    this.queryAppid = String(this.$route.query.appid || '').trim()
    if (this.queryAppid) {
      this.fetchKf()
    }
    this.init()
  },
  beforeDestroy() {
    this.stopQrPoll()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // from.matched 为空 = 直接打开链接/刷新进入（START_LOCATION），不是应用内跳转
      vm.canGoBack = !!(from && from.matched && from.matched.length)
    })
  },
  methods: {
    async init() {
      await this.fetchSellers()
      if (this.sellers.length) {
        await this.fetchPackages()
        await this.fetchSummary()
        await this.fetchCdks(1)
      }
    },
    copyAllCodes() {
      if (!this.generatedCodes.length) return
      this.copyText(this.generatedCodes.join('\n'), '已复制 ' + this.generatedCodes.length + ' 张卡密')
    },
    // 套餐维度统计（可选接口，失败不影响列表，只留控制台）
    async fetchSummary() {
      try {
        const res = await this.$ajax.get('api/v1/proxy/cdks/summary', {
          params: { appid: this.currentAppid },
        })
        const body = res.data || {}
        if (body.status !== 200) throw new Error(body.msg || '统计失败')
        this.packageSummary = body.data || []
        this.summaryTotal = Number(body.total || 0)
      } catch (error) {
        this.packageSummary = []
        this.summaryTotal = 0
        console.error('获取套餐统计失败:', error)
      }
    },
    goBack() {
      this.$router.back()
    },
    async fetchSellers() {
      this.loadingSellers = true
      try {
        if (!window.localStorage.getItem('token')) {
          // 匿名访问：直接按"未解锁"展示，不发请求（否则会被 401 带到登录页）
          this.sellers = []
          this.canActivate = false
          return
        }
        // skipAuthRedirect：token 过期也按未解锁展示，不要被全局拦截器带去登录页
        const res = await this.$ajax.get('api/v1/proxy/cdks/sellers', { skipAuthRedirect: true })
        const body = res.data || {}
        const list = body.data || []
        this.sellers = list
        this.canActivate = !!body.can_activate
        if (list.length) {
          this.currentAppid = list[0].appid
        }
      } catch (error) {
        this.sellers = []
        this.canActivate = false
        // 未登录 / token 失效：按"未解锁"展示即可（页面允许匿名访问），
        // 不弹错误提示；真正要操作时再引导登录
        if (!this.isAuthError(error)) {
          this.$message.error(this.errorText(error, '获取权限失败'))
        }
      } finally {
        this.loadingSellers = false
      }
    },
    // 需要登录的操作：跳登录页并带回跳地址
    redirectToLogin() {
      if (this.$route.path === '/login') return
      this.$router.replace({
        path: '/login',
        query: { redirect: this.$route.fullPath || '/buy-cdk' },
      }, () => {}, () => {})
    },
    isAuthError(error) {
      if (error && error.response && error.response.status === 401) return true
      const data = error && error.response && error.response.data
      if (data && Number(data.status) === 100) {
        return /token|登录/i.test(String(data.msg || data.detail || ''))
      }
      return false
    },
    async activate() {
      if (this.activating) return
      this.activating = true
      try {
        const res = await this.$ajax.post('api/v1/proxy/cdks/activate')
        if (!res.data || res.data.status !== 200) throw new Error(res.data && res.data.msg)
        this.$message.success((res.data && res.data.msg) || '开通成功')
        await this.init()
      } catch (error) {
        this.$message.error(this.errorText(error, '开通失败'))
      } finally {
        this.activating = false
      }
    },
    // 代理直接开通；普通用户走激活码弹窗；URL 指定了代理则引导联系客服
    onPrimaryAction() {
      // 客服二维码是给"还没开通的人"看的，不需要登录
      if (this.queryAppid) {
        this.openKfModal()
        return
      }
      if (this.requireLogin()) return
      if (this.canActivate) {
        this.activate()
      } else {
        this.openCodeModal()
      }
    },
    openKfModal() {
      this.kfVisible = true
      if (!this.kfUrl) {
        this.fetchKf()
      }
    },
    // 取该代理的客服二维码（接口可匿名访问）
    async fetchKf() {
      this.kfLoading = true
      try {
        const res = await this.$ajax.get('api/v1/users/app-config', {
          params: { appid: this.queryAppid },
          skipAuthRedirect: true,
        })
        const body = res.data || {}
        const data = body.data || {}
        this.kfUrl = (body.status === 200 && data.kf) ? data.kf : ''
      } catch (error) {
        this.kfUrl = ''
      } finally {
        this.kfLoading = false
      }
    },
    // 未登录时引导去登录（页面本身允许匿名浏览）
    requireLogin() {
      if (window.localStorage.getItem('token')) return false
      this.$message.warning('请先登录')
      this.redirectToLogin()
      return true
    },
    openCodeModal() {
      this.codeValue = ''
      this.codeError = ''
      this.codeVisible = true
      var self = this
      this.$nextTick(function () {
        if (self.$refs.codeInput) self.$refs.codeInput.focus()
      })
    },
    closeCodeModal() {
      if (this.codeSubmitting) return
      this.codeVisible = false
    },
    async submitCode() {
      if (this.codeSubmitting) return
      var code = (this.codeValue || '').trim()
      if (!code) {
        this.codeError = '请输入激活码'
        return
      }
      this.codeSubmitting = true
      this.codeError = ''
      try {
        const res = await this.$ajax.post('api/v1/proxy/cdks/activate-by-code', { code: code })
        if (!res.data || res.data.status !== 200) {
          throw new Error((res.data && res.data.msg) || '激活失败')
        }
        this.$message.success((res.data && res.data.msg) || '激活成功')
        this.codeVisible = false
        await this.init()
      } catch (error) {
        this.codeError = this.errorText(error, '激活失败')
      } finally {
        this.codeSubmitting = false
      }
    },
    async fetchPackages() {
      this.loadingPackages = true
      try {
        const res = await this.$ajax.get('api/v1/proxy/cdks/packages', {
          params: { appid: this.currentAppid },
        })
        if (!res.data || res.data.status !== 200) throw new Error(res.data && res.data.msg)
        this.packages = res.data.data || []
        this.rate = res.data.rate || 1
        this.balance = res.data.balance || 0
        if (!this.packages.length) {
          this.packageId = ''
          this.selectedPackage = null
        } else {
          var exists = this.packages.some(function (item) { return item.id === this.packageId }, this)
          if (!exists) this.selectPackage(this.packages[0].id)
        }
      } catch (error) {
        this.packages = []
        this.$message.error(this.errorText(error, '获取套餐失败'))
      } finally {
        this.loadingPackages = false
      }
    },
    async fetchCdks(page) {
      this.loading = true
      const target = page || this.page
      try {
        const params = {
          page: target,
          page_size: this.pageSize,
          keyword: this.keyword,
          search_type: this.searchType,
          usage_status: this.usageStatus,
          appid: this.currentAppid,
        }
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_date = this.dateRange[0]
          params.end_date = this.dateRange[1]
        }
        if (this.filterVipid !== '') {
          params.vipid = this.filterVipid
        }
        const res = await this.$ajax.get('api/v1/proxy/cdks', { params })
        if (!res.data || res.data.status !== 200) throw new Error(res.data && res.data.msg)
        const data = res.data.data || {}
        this.records = data.items || []
        this.total = data.total || 0
        this.page = target
        this.selectedRows = []
      } catch (error) {
        this.$message.error(this.errorText(error, '获取兑换码失败'))
      } finally {
        this.loading = false
      }
    },
    handleSellerChange() {
      this.packageId = ''
      this.selectedPackage = null
      this.searchCdks()
      this.fetchPackages()
    },
    searchCdks() {
      this.fetchCdks(1)
    },
    resetFilters() {
      this.filterVipid = ''
      this.usageStatus = 'all'
      this.searchType = 'name'
      this.keyword = ''
      this.dateRange = []
      this.fetchCdks(1)
    },
    changePage(page) {
      this.fetchCdks(page)
    },
    selectPackage(id) {
      this.packageId = id
      var list = this.packages || []
      for (var i = 0; i < list.length; i++) {
        if (list[i].id === id) {
          this.selectedPackage = list[i]
          return
        }
      }
      this.selectedPackage = null
    },
    buy() {
      if (!this.selectedPackage || this.buying || this.confirming) return
      if (this.requireLogin()) return
      if (this.payMethod === 'alipay') return this.confirmAlipay()
      const pkg = this.selectedPackage
      const amount = (pkg.pay * this.num).toFixed(2)
      const tip = pkg.license
        ? '确认购买「' + pkg.name + '」× ' + this.num + '，需扣除 ¥' + amount + '？卡密不会自动开通，需发给下级激活。'
        : '确认购买「' + pkg.name + '」× ' + this.num + '，需扣除 ¥' + amount + '？'
      this.confirming = true
      this.$confirm(
        tip,
        '购买确认',
        { confirmButtonText: '确认购买', cancelButtonText: '取消', type: 'warning' }
      ).then(() => {
        this.confirming = false
        this.doBuy()
      }).catch(() => {
        this.confirming = false
      })
    },
    async doBuy() {
      // 「兑换码代理开通」走卡密分支：只发代理卡，不生成兑换码
      if (this.selectedPackage && this.selectedPackage.license) return this.doBuyLicense()
      this.buying = true
      try {
        const res = await this.$ajax.post('api/v1/proxy/cdks/purchase', {
          vip_id: this.selectedPackage.id,
          num: this.num,
          remark: this.remark || '',
          appid: this.currentAppid,
        })
        if (!res.data || res.data.status !== 200) throw new Error(res.data && res.data.msg)
        const codes = res.data.data || []
        this.$message.success('已生成 ' + codes.length + ' 个兑换码')
        this.remark = ''
        this.num = 1
        await this.fetchPackages()
        await this.fetchSummary()
        await this.fetchCdks(1)
      } catch (error) {
        this.$message.error(this.errorText(error, '购买失败'))
      } finally {
        this.buying = false
      }
    },
    // 购买「兑换码代理开通」：扣余额、拿到 N 张代理卡（不自动激活）
    async doBuyLicense() {
      this.buying = true
      try {
        const res = await this.$ajax.post('api/v1/proxy/cdks/licenses/purchase', {
          num: this.num,
          remark: this.remark || '',
          appid: this.currentAppid,
        })
        if (!res.data || res.data.status !== 200) throw new Error(res.data && res.data.msg)
        const data = res.data.data || {}
        this.generatedCodes = data.codes || []
        this.generatedInfo = {
          app_name: data.app_name || '',
          discount: data.discount,
          unit_price: data.unit_price,
          price: data.price,
        }
        this.codesVisible = true
        this.$message.success(res.data.msg || '已生成代理卡')
        this.remark = ''
        this.num = 1
        await this.fetchPackages()
        await this.fetchCdks(1)
      } catch (error) {
        this.$message.error(this.errorText(error, '购买失败'))
      } finally {
        this.buying = false
      }
    },
    // ===== 支付宝扫码支付 =====
    confirmAlipay() {
      const pkg = this.selectedPackage
      const amount = (pkg.pay * this.num).toFixed(2)
      const tip = pkg.license
        ? '确认购买「' + pkg.name + '」× ' + this.num + '，应付 ¥' + amount + '？支付后卡密需发给下级激活。'
        : '确认购买「' + pkg.name + '」× ' + this.num + '，应付 ¥' + amount + '？'
      this.confirming = true
      this.$confirm(tip, '支付宝支付', {
        confirmButtonText: '去支付', cancelButtonText: '取消', type: 'warning',
      }).then(() => {
        this.confirming = false
        this.doBuyAlipay()
      }).catch(() => {
        this.confirming = false
      })
    },
    async doBuyAlipay() {
      const pkg = this.selectedPackage
      this.buying = true
      try {
        const res = await this.$ajax.post('api/v1/proxy/cdks/alipay/order', {
          kind: pkg.license ? 'license' : 'cdk',
          vip_id: pkg.license ? 0 : pkg.id,
          num: this.num,
          appid: this.currentAppid,
        })
        if (!res.data || res.data.status !== 200) throw new Error(res.data && res.data.msg)
        const data = res.data.data || {}
        this.qrData = {
          ordernum: data.ordernum,
          amount: data.amount,
          subject: data.subject,
          num: data.num,
          kind: data.kind,
        }
        this.qrVisible = true
        this.renderQr(data.qr_code)
        this.startQrPoll()
      } catch (error) {
        this.$message.error(this.errorText(error, '下单失败'))
      } finally {
        this.buying = false
      }
    },
    renderQr(url) {
      // qrcodejs2 是往容器里 append 节点，必须先清空，否则再次支付会叠出第二个二维码
      const box = document.getElementById('cdk-alipay-qrcode')
      if (box) {
        box.innerHTML = ''
      }
      if (this.qrcode_m) {
        this.qrcode_m.clear()
        this.qrcode_m = null
      }
      this.qrGenerating = true
      // 等弹窗渲染完再画二维码
      this.$nextTick(() => {
        setTimeout(() => {
          const target = document.getElementById('cdk-alipay-qrcode')
          if (!target) {
            this.qrGenerating = false
            return
          }
          target.innerHTML = ''
          try {
            this.qrcode_m = new QRCode(target, {
              width: 200,
              height: 200,
              text: url,
              colorDark: '#000000',
              colorLight: '#ffffff',
              correctLevel: QRCode.CorrectLevel.H,
            })
            // 生成的 canvas/img 是 JS 动态插入的，带不上 scoped 属性，
            // scoped 样式匹配不到，这里用内联样式把尺寸兜死
            const nodes = target.querySelectorAll('canvas, img, table')
            for (let i = 0; i < nodes.length; i++) {
              nodes[i].style.width = '200px'
              nodes[i].style.height = '200px'
            }
          } catch (error) {
            console.error('二维码生成失败:', error)
          } finally {
            this.qrGenerating = false
          }
        }, 80)
      })
    },
    startQrPoll() {
      this.stopQrPoll()
      this.qrTimer = setInterval(this.checkQrPaid, 3000)
    },
    stopQrPoll() {
      if (this.qrTimer) {
        clearInterval(this.qrTimer)
        this.qrTimer = null
      }
    },
    // 轮询支付状态；已支付则自动领码并弹卡密窗
    async checkQrPaid() {
      if (!this.qrData.ordernum || this.qrChecking) return
      this.qrChecking = true
      try {
        const res = await this.$ajax.post('api/v1/users/pay/payok', { ordernum: this.qrData.ordernum })
        if (res.data && res.data.status === 200) {
          this.stopQrPoll()
          await this.claimAlipayOrder()
        }
      } catch (error) {
        // 轮询失败不打扰用户，等下一轮
      } finally {
        this.qrChecking = false
      }
    },
    async claimAlipayOrder() {
      try {
        const res = await this.$ajax.post('api/v1/proxy/cdks/orders/' + this.qrData.ordernum + '/claim')
        if (!res.data || res.data.status !== 200) throw new Error(res.data && res.data.msg)
        const data = res.data.data || {}
        this.generatedCodes = data.codes || []
        this.generatedInfo = {
          app_name: data.app_name || '',
          discount: data.discount,
          unit_price: data.unit_price,
          price: data.amount,
        }
        this.qrVisible = false
        this.codesVisible = true
        this.$message.success(res.data.msg || '支付成功')
        await this.fetchPackages()
        await this.fetchSummary()
        await this.fetchCdks(1)
      } catch (error) {
        this.$message.error(this.errorText(error, '领取失败，请联系客服'))
      }
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows || []
    },
    // 已使用的兑换码整行置灰
    usedRowClass({ row }) {
      return row && row.status === 1 ? '' : 'cdk-row-used'
    },
    openBatchRedeem() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先选择兑换码')
        return
      }
      if (this.selectedRows.some(function (item) { return item.license })) {
        this.$message.warning('代理卡不能批量兑换，请只选择兑换码')
        return
      }
      var unused = this.selectedRows.filter(function (item) { return item.status === 1 })
      if (unused.length !== this.selectedRows.length) {
        this.$message.warning('批量兑换只能选择未使用的兑换码')
        return
      }
      this.batchRows = this.selectedRows.slice()
      this.batchUsernames = ''
      this.batchResults = []
      this.batchVisible = true
    },
    async submitBatch() {
      if (!this.canSubmitBatch) return
      this.batchLoading = true
      try {
        var items = this.batchRows.map(function (item, index) {
          return { cdk_id: item.id, username: this.parsedUsernames[index] }
        }, this)
        const res = await this.$ajax.post('api/v1/proxy/cdks/batch-redeem', {
          items: items,
          appid: this.currentAppid,
        })
        if (!res.data || res.data.status !== 200) throw new Error(res.data && res.data.msg)
        const data = res.data.data || {}
        this.batchResults = data.results || []
        this.$message.success('兑换完成：成功 ' + (data.success_count || 0) + ' 个，失败 ' + (data.failed_count || 0) + ' 个')
        await this.fetchPackages()
        await this.fetchCdks(this.page)
      } catch (error) {
        this.$message.error(this.errorText(error, '批量兑换失败'))
      } finally {
        this.batchLoading = false
      }
    },
    openBatchRemark() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先选择兑换码')
        return
      }
      if (this.selectedRows.some(function (item) { return item.license })) {
        this.$message.warning('代理卡不支持批量改备注，请只选择兑换码')
        return
      }
      this.batchRemark = ''
      this.remarkVisible = true
    },
    async submitBatchRemark() {
      this.remarkLoading = true
      try {
        const res = await this.$ajax.post('api/v1/proxy/cdks/batch-remark', {
          cdk_ids: this.selectedRows.map(function (item) { return item.id }),
          remark: this.batchRemark || '',
          appid: this.currentAppid,
        })
        if (!res.data || res.data.status !== 200) throw new Error(res.data && res.data.msg)
        this.$message.success('已修改 ' + ((res.data.data && res.data.data.updated_count) || 0) + ' 个兑换码备注')
        this.remarkVisible = false
        await this.fetchCdks(this.page)
      } catch (error) {
        this.$message.error(this.errorText(error, '修改备注失败'))
      } finally {
        this.remarkLoading = false
      }
    },
    copySelected() {
      var text = this.selectedRows.map(function (item) { return item.name }).join('\n')
      this.copyText(text, '已复制 ' + this.selectedRows.length + ' 个兑换码')
    },
    exportSelected() {
      var self = this
      var lines = ['卡密\t类型\t内容\t状态\t激活用户\t备注']
      this.selectedRows.forEach(function (item) {
        lines.push([item.name, item.license ? '代理卡' : '兑换码', self.recordMeta(item),
          item.status === 1 ? '未使用' : (item.license ? '已激活' : '已使用'),
          item.username || '', item.remark || ''].join('\t'))
      })
      var blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
      var link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '兑换码_' + new Date().getTime() + '.txt'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    copyText(text, message) {
      var area = document.createElement('textarea')
      area.value = text
      area.style.position = 'fixed'
      area.style.opacity = '0'
      document.body.appendChild(area)
      area.select()
      try {
        document.execCommand('copy')
        this.$message.success(message || '已复制')
      } catch (error) {
        this.$message.warning('复制失败，请手动复制')
      }
      document.body.removeChild(area)
    },
    packageMeta(item) {
      if (item.type === 1) return '点数：' + item.surplus + ' 点'
      return '有效期：' + item.surplus + ' 个月'
    },
    // 记录列表接口返回的是 fs(点数/月数) 与 limtsecond(每日限制)，字段名与套餐接口不同
    // 代理卡（权限卡）没有套餐字段，改显示适用软件与折扣
    recordMeta(item) {
      if (item.license) {
        return '开通兑换码权限 · ' + (item.app_name || '') + ' · ' + this.formatDiscount(item.discount)
      }
      var amount = item.fs || 0
      if (item.type === 1) return '点数：' + amount + ' 点'
      var limit = item.limtsecond ? '每日 ' + item.limtsecond + ' 次' : '每日不限'
      return '有效期：' + amount + ' 个月 · ' + limit
    },
    originPay(item) {
      if (!this.rate) return item.pay
      return (item.pay / this.rate).toFixed(2)
    },
    formatDiscount(value) {
      var num = Number(value === undefined || value === null ? 1 : value)
      if (num >= 1) return '原价'
      return (num * 10).toFixed(1).replace(/\.0$/, '') + ' 折'
    },
    formatNumber(value) {
      return Number(value || 0).toFixed(2)
    },
    formatDate(timestamp) {
      if (!timestamp) return '—'
      var date = new Date(timestamp * 1000)
      var pad = function (n) { return n < 10 ? '0' + n : '' + n }
      return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
        + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes())
    },
    errorText(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.detail) {
        return error.response.data.detail
      }
      return (error && error.message) || fallback
    },
  },
}
</script>

<style scoped>
.buy-cdk-page {
  min-height: 100vh;
  background: #f5f7fa;
  color: #1f2937;
}

/* 顶栏 */
.cdk-topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  border-bottom: 1px solid #e6ebf2;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .03);
}

.cdk-topbar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  max-width: 1128px;
  margin: 0 auto;
  padding: 0 24px;
  height: 60px;
}

.cdk-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.cdk-brand-icon {
  font-size: 20px;
  color: #1a73e8;
}

.cdk-brand-text {
  font-size: 17px;
  font-weight: 600;
  color: #111827;
}

.cdk-rate-tag {
  padding: 1px 8px;
  border-radius: 10px;
  background: #fff4e6;
  color: #d97706;
  font-size: 12px;
  font-weight: 600;
}

.cdk-topbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.cdk-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid #d8e0ea;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  font-size: 16px;
  cursor: pointer;
  transition: border-color .2s, color .2s, background .2s;
}

.cdk-back:hover {
  border-color: #1a73e8;
  color: #1a73e8;
  background: #f5f9ff;
}

/* 主体 */
.cdk-body {
  max-width: 1128px;
  margin: 0 auto;
  padding: 28px 24px 72px;
}

.cdk-loading {
  min-height: 260px;
}

/* ===== 未开通：宣传式引导 ===== */
.cdk-lock {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, .9fr);
  gap: 18px;
  margin-top: 24px;
}

.cdk-lock-hero {
  position: relative;
  padding: 34px 32px 30px;
  border-radius: 16px;
  background: linear-gradient(135deg, #0b3aa8 0%, #1a73e8 52%, #5b6bf0 100%);
  box-shadow: 0 18px 44px rgba(26, 115, 232, .26);
  color: #fff;
  overflow: hidden;
}

/* 右上角光晕，提升质感 */
.cdk-lock-hero::after {
  content: '';
  position: absolute;
  top: -150px;
  right: -120px;
  width: 340px;
  height: 340px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, .3), rgba(255, 255, 255, 0) 70%);
  pointer-events: none;
}

.cdk-lock-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid rgba(255, 255, 255, .45);
  border-radius: 999px;
  background: rgba(255, 255, 255, .16);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .3px;
  backdrop-filter: blur(2px);
}

.cdk-lock-badge i {
  font-size: 13px;
  color: #ffe08a;
}

.cdk-lock-title {
  position: relative;
  margin: 16px 0 8px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: .5px;
}

.cdk-lock-sub {
  position: relative;
  margin: 0;
  color: rgba(255, 255, 255, .82);
  font-size: 13px;
}

.cdk-lock-benefits {
  position: relative;
  display: grid;
  gap: 12px;
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
}

.cdk-lock-benefits li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.cdk-lock-benefits li > i {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: rgba(255, 255, 255, .18);
  color: #fff;
  font-size: 14px;
}

.cdk-lock-benefit-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cdk-lock-benefit-text b {
  font-size: 14px;
  font-weight: 600;
}

.cdk-lock-benefit-text span {
  color: rgba(255, 255, 255, .78);
  font-size: 12px;
  line-height: 1.6;
}

.cdk-lock-cta {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 26px;
  padding: 14px 20px;
  border: 0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(6, 30, 80, .22);
  color: #1250c4;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: transform .2s, box-shadow .2s;
}

.cdk-lock-cta i {
  font-size: 16px;
}

.cdk-lock-cta:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 14px 30px rgba(6, 30, 80, .28);
}

.cdk-lock-cta:disabled {
  opacity: .72;
  cursor: not-allowed;
}

.cdk-lock-hint {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  margin: 14px 0 0;
  color: rgba(255, 255, 255, .78);
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
}

/* 权益预览（示意） */
.cdk-lock-preview {
  display: flex;
  flex-direction: column;
  padding: 20px 20px 16px;
  border: 1px solid #e6ebf2;
  border-radius: 16px;
  background: #fff;
}

.cdk-lock-preview-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  color: #1f2937;
  font-size: 13px;
  font-weight: 600;
}

.cdk-lock-preview-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
}

.cdk-lock-preview-table {
  display: grid;
  gap: 10px;
}

.cdk-lock-preview-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1.2fr;
  align-items: center;
  gap: 10px;
}

.cdk-lock-preview-row--head {
  color: #94a3b8;
  font-size: 12px;
}

.cdk-lock-bar {
  height: 10px;
  border-radius: 5px;
  background: linear-gradient(90deg, #eef2f7 25%, #f7f9fc 50%, #eef2f7 75%);
  background-size: 200% 100%;
  animation: cdk-lock-shimmer 1.6s infinite;
}

.cdk-lock-bar.w1 { width: 92%; }
.cdk-lock-bar.w2 { width: 70%; }
.cdk-lock-bar.w3 { width: 58%; }
.cdk-lock-bar.w4 { width: 80%; }

@keyframes cdk-lock-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.cdk-lock-preview-tip {
  margin: 16px 0 0;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.7;
}

@media (max-width: 900px) {
  .cdk-lock {
    grid-template-columns: minmax(0, 1fr);
    margin-top: 14px;
  }

  .cdk-lock-hero {
    padding: 24px 20px 22px;
  }

  .cdk-lock-title {
    font-size: 21px;
  }

  .cdk-lock-preview-row {
    grid-template-columns: 1.2fr .9fr .9fr 1fr;
    gap: 6px;
  }
}

/* ===== 联系客服开通弹窗 ===== */
.buy-cdk-page >>> .cdk-kf-dialog {
  width: 380px;
  margin-top: 10vh !important;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, .24);
}

.buy-cdk-page >>> .cdk-kf-dialog .el-dialog__header {
  display: none;
}

.buy-cdk-page >>> .cdk-kf-dialog .el-dialog__body {
  padding: 0;
}

.cdk-kf {
  position: relative;
  padding: 30px 26px 26px;
  text-align: center;
}

.cdk-kf-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #cbd5e1;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: background .2s, color .2s;
}

.cdk-kf-close:hover {
  background: #f1f5f9;
  color: #64748b;
}

.cdk-kf-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  height: 62px;
  margin: 0 auto 16px;
  border-radius: 20px;
  background: linear-gradient(135deg, #1a73e8 0%, #4d8bff 100%);
  box-shadow: 0 12px 26px rgba(26, 115, 232, .3);
}

.cdk-kf-badge i {
  font-size: 27px;
  color: #fff;
}

.cdk-kf-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.cdk-kf-desc {
  margin: 0 0 20px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.7;
}

.cdk-kf-qr {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 210px;
  height: 210px;
  margin: 0 auto;
  padding: 10px;
  border: 1px solid #e6ebf2;
  border-radius: 14px;
  background: #f8fafc;
}

.cdk-kf-qr >>> .el-image,
.cdk-kf-qr >>> .el-image__inner {
  width: 100%;
  height: 100%;
}

.cdk-kf-qr >>> .el-image__inner {
  border-radius: 8px;
  cursor: zoom-in;
}

.cdk-kf-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12px;
}

.cdk-kf-empty i {
  font-size: 26px;
}

.cdk-kf-hint {
  margin: 12px 0 18px;
  color: #94a3b8;
  font-size: 12px;
}

.cdk-kf-hint i {
  margin-right: 4px;
}

.cdk-kf-btn {
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #1a73e8 0%, #4d8bff 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(26, 115, 232, .26);
  transition: transform .15s, box-shadow .2s;
}

.cdk-kf-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(26, 115, 232, .32);
}

.cdk-kf-btn:active {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .buy-cdk-page >>> .cdk-kf-dialog {
    width: 86vw !important;
    margin-top: 12vh !important;
  }

  .cdk-kf {
    padding: 26px 18px 22px;
  }
}

/* ===== 激活码弹窗（自绘） ===== */
.buy-cdk-page >>> .cdk-code-dialog {
  width: 440px;
  margin-top: 12vh !important;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, .24);
}

.buy-cdk-page >>> .cdk-code-dialog .el-dialog__header {
  display: none;
}

.buy-cdk-page >>> .cdk-code-dialog .el-dialog__body {
  padding: 0;
}

.code-modal {
  position: relative;
  padding: 34px 32px 30px;
  text-align: center;
}

.code-modal-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #cbd5e1;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: background .2s, color .2s;
}

.code-modal-close:hover {
  background: #f1f5f9;
  color: #64748b;
}

.code-modal-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: 0 auto 18px;
  border-radius: 20px;
  background: linear-gradient(135deg, #1a73e8 0%, #4d8bff 100%);
  box-shadow: 0 12px 26px rgba(26, 115, 232, .32);
}

.code-modal-badge i {
  font-size: 28px;
  color: #fff;
}

.code-modal-title {
  margin: 0 0 10px;
  font-size: 19px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: .5px;
}

.code-modal-desc {
  margin: 0 0 24px;
  font-size: 13px;
  line-height: 1.7;
  color: #64748b;
}

.code-modal-field {
  border-radius: 12px;
}

.code-modal-input {
  box-sizing: border-box;
  width: 100%;
  height: 52px;
  padding: 0 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  color: #0f172a;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 2px;
  text-align: center;
  outline: none;
  transition: border-color .2s, background .2s, box-shadow .2s;
}

.code-modal-input::placeholder {
  color: #c3ccda;
  font-weight: 400;
  letter-spacing: 1px;
}

.code-modal-input:focus {
  border-color: #1a73e8;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(26, 115, 232, .12);
}

.code-modal-field.is-error .code-modal-input {
  border-color: #f56c6c;
  background: #fff;
}

.code-modal-field.is-error .code-modal-input:focus {
  box-shadow: 0 0 0 4px rgba(245, 108, 108, .12);
}

.code-modal-hint {
  min-height: 18px;
  margin: 8px 0 18px;
  font-size: 12px;
  line-height: 18px;
  color: #94a3b8;
}

.code-modal-hint.is-error {
  color: #f56c6c;
}

.code-modal-submit {
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #1a73e8 0%, #4d8bff 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(26, 115, 232, .26);
  transition: transform .15s, box-shadow .2s, opacity .2s;
}

.code-modal-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(26, 115, 232, .32);
}

.code-modal-submit:active:not(:disabled) {
  transform: translateY(0);
}

.code-modal-submit:disabled {
  opacity: .45;
  cursor: not-allowed;
  box-shadow: none;
}

/* 区块 */
.cdk-section + .cdk-section {
  margin-top: 32px;
}

.cdk-section-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}

.cdk-section-title {
  position: relative;
  margin: 0;
  padding-left: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.cdk-section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: #1a73e8;
}

.cdk-section-sub {
  font-size: 13px;
  color: #94a3b8;
}

.cdk-seller-label {
  font-size: 13px;
  color: #64748b;
}

.cdk-seller-switch {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cdk-seller-single {
  font-size: 13px;
  color: #64748b;
}

.cdk-seller-single b {
  color: #1f2937;
}

/* 卡片 */
.cdk-card {
  padding: 24px;
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .04);
}

/* 表单行 */
.cdk-field {
  display: flex;
  align-items: center;
  margin-bottom: 22px;
}

.cdk-field:last-of-type {
  margin-bottom: 0;
}

.cdk-field-block {
  align-items: flex-start;
}

.cdk-field-label {
  flex-shrink: 0;
  width: 88px;
  font-size: 14px;
  color: #475569;
}

.cdk-remark-input {
  width: 320px;
}

/* 套餐卡 */
.cdk-package-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(152px, 1fr));
  gap: 10px;
  width: 100%;
}

.cdk-package {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 12px;
  text-align: left;
  background: #fff;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color .18s, background .18s, box-shadow .18s;
}

.cdk-package:hover,
.cdk-package.active {
  border-color: #1a73e8;
  background: #f5f9ff;
}

.cdk-package.active {
  box-shadow: 0 0 0 3px rgba(26, 115, 232, .1);
}

.cdk-package-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.cdk-package-name {
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cdk-package-price {
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1a73e8;
}

.cdk-package-origin {
  font-size: 11px;
  color: #b0b8c4;
  text-decoration: line-through;
}

.cdk-package-meta {
  font-size: 11px;
  color: #64748b;
}

.cdk-nopackage {
  padding: 16px 0;
  font-size: 13px;
  color: #94a3b8;
}

/* 支付方式：两张可选卡片 */
.cdk-pay-methods {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  max-width: 470px;
}

.cdk-pay-method {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  text-align: left;
  background: #fff;
  border: 1px solid #dbe4ef;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color .18s, background .18s, box-shadow .18s;
}

.cdk-pay-method > i {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: #f1f5f9;
  color: #94a3b8;
  font-size: 17px;
  transition: background .18s, color .18s;
}

.cdk-pay-method:hover {
  border-color: #a9c7f5;
  background: #f8fbff;
}

.cdk-pay-method.active {
  border-color: #1a73e8;
  background: #f5f9ff;
  box-shadow: 0 0 0 3px rgba(26, 115, 232, .1);
}

.cdk-pay-method.active > i {
  background: #1a73e8;
  color: #fff;
}

.cdk-pay-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.cdk-pay-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.cdk-pay-desc {
  overflow: hidden;
  font-size: 11px;
  color: #94a3b8;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 「兑换码代理开通」与普通套餐同色，只用一个中性小标签区分 */
.cdk-package-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 8px;
  background: #eef2f7;
  color: #64748b;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  vertical-align: 1px;
}

/* 支付宝扫码弹窗 */
.cdk-qr {
  text-align: center;
}

.cdk-qr-amount {
  font-size: 14px;
  color: #475569;
}

.cdk-qr-amount b {
  font-size: 22px;
  color: #d97706;
}

.cdk-qr-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #94a3b8;
}

.cdk-qr-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 200px;
  margin: 16px auto 0;
  border: 1px solid #e6ebf2;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

/* qrcodejs2 动态生成的节点拿不到 scoped 属性，必须用深度选择器 */
.cdk-qr-box >>> canvas,
.cdk-qr-box >>> img,
.cdk-qr-box >>> table {
  width: 200px !important;
  height: 200px !important;
}

.cdk-qr-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 14px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}

/* 代理卡结果弹窗 */
.cdk-codes-tip {
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
}

.cdk-codes-tip b {
  color: #1f2937;
}

.cdk-codes-sep {
  margin: 0 6px;
  color: #cbd5e1;
}

.cdk-codes {
  display: grid;
  gap: 8px;
  max-height: 280px;
  margin-top: 14px;
  overflow-y: auto;
}

.cdk-codes-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  background: #fafcff;
  font-size: 13px;
}

.cdk-codes-index {
  flex-shrink: 0;
  color: #94a3b8;
}

.cdk-codes-name {
  flex: 1;
  min-width: 0;
  color: #1f2937;
  font-family: Consolas, Menlo, monospace;
  word-break: break-all;
}

.cdk-codes-copy {
  flex-shrink: 0;
  padding: 4px 10px;
  border: 1px solid #d8e0ea;
  border-radius: 6px;
  background: #fff;
  color: #1a73e8;
  font-size: 12px;
  cursor: pointer;
  transition: border-color .2s, background .2s;
}

.cdk-codes-copy:hover {
  border-color: #1a73e8;
  background: #f5f9ff;
}

/* 操作区 */
.cdk-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 26px;
  padding-top: 22px;
  border-top: 1px solid #eef2f7;
}

.cdk-actions-tip {
  font-size: 13px;
  color: #64748b;
}

.cdk-actions-tip b {
  color: #1a73e8;
}

/* 工具条 */
.cdk-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.cdk-toolbar-actions {
  margin-bottom: 14px;
}

/* 筛选表单：带标签，避免多个下拉看不出用途（label/content 是 el-form-item 内部节点，必须用 >>> 穿透 scoped） */
.cdk-filters {
  margin-bottom: 4px;
}

.cdk-filters .el-form-item {
  margin-right: 18px;
  margin-bottom: 12px;
}

.cdk-filters >>> .el-form-item__label {
  padding-right: 8px;
  color: #64748b;
  font-size: 13px;
}

.cdk-filters >>> .el-form-item__content {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* el-form-item__content 自带的 clearfix 伪元素会被当成 flex/grid 子项，挤掉一整列 */
.cdk-filters >>> .el-form-item__content::before,
.cdk-filters >>> .el-form-item__content::after {
  display: none;
}

.cdk-w-sm {
  width: 112px;
}

.cdk-w-pkg {
  width: 190px;
}

/* 套餐下拉选项右侧的数量 */
.cdk-option-count {
  float: right;
  margin-left: 18px;
  color: #94a3b8;
  font-size: 12px;
}

.cdk-w-md {
  width: 220px;
}

.cdk-w-date {
  width: 268px;
}

.cdk-selected-tip {
  font-size: 13px;
  color: #64748b;
}

.cdk-link {
  color: #1a73e8;
  cursor: pointer;
}

.cdk-link:hover {
  text-decoration: underline;
}

/* 卡密类型小标签（兑换码 / 代理卡） */
.cdk-type-tag {
  display: inline-block;
  margin-right: 6px;
  padding: 0 5px;
  border-radius: 8px;
  background: #eef2f7;
  color: #64748b;
  font-size: 11px;
  line-height: 16px;
}

.cdk-type-tag.is-license {
  background: #e8f0fe;
  color: #1a73e8;
}

.cdk-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 50%;
  vertical-align: middle;
}

.cdk-dot.on {
  background: #52c41a;
}

.cdk-dot.off {
  background: #c3ccda;
}

/* 已使用的兑换码：整行置灰（el-table 内部节点需用 >>> 穿透 scoped） */
.buy-cdk-page >>> .el-table .cdk-row-used td,
.buy-cdk-page >>> .el-table .cdk-row-used .cell {
  color: #b0b8c4;
}

.buy-cdk-page >>> .el-table .cdk-row-used .cdk-link {
  color: #b0b8c4;
}

.buy-cdk-page >>> .el-table .cdk-row-used .cdk-dot {
  background: #dfe4ea;
}

.cdk-pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

/* 弹窗 */
.cdk-batch-input {
  margin-top: 16px;
}

.cdk-batch-count {
  margin-top: 12px;
  font-size: 13px;
  color: #64748b;
}

.cdk-batch-count.error,
.cdk-batch-item .error {
  color: #f56c6c;
}

.cdk-batch-item .success {
  color: #67c23a;
}

.cdk-batch-preview {
  display: grid;
  gap: 8px;
  max-height: 260px;
  margin-top: 14px;
  overflow-y: auto;
}

.cdk-batch-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  background: #fafcff;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  font-size: 13px;
}

/* 响应式 */
@media (max-width: 768px) {
  .cdk-topbar-inner {
    padding: 0 14px;
    height: 54px;
  }

  .cdk-body {
    padding: 18px 12px 48px;
  }

  .cdk-card {
    padding: 16px 14px;
    border-radius: 10px;
  }

  .cdk-section + .cdk-section {
    margin-top: 24px;
  }

  .cdk-field,
  .cdk-field-block {
    display: block;
  }

  .cdk-field-label {
    width: auto;
    margin-bottom: 10px;
  }

  .cdk-remark-input {
    width: 100%;
  }

  .cdk-package-grid {
    grid-template-columns: repeat(auto-fill, minmax(128px, 1fr));
    gap: 8px;
  }

  .buy-cdk-page >>> .cdk-code-dialog {
    width: 92vw !important;
    margin-top: 16vh !important;
  }

  .code-modal {
    padding: 28px 20px 24px;
  }

  /* 手机端筛选区：两列紧凑排布，去掉元素默认 40px 行高带来的一片留白 */
  .cdk-filters {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .cdk-filters .el-form-item {
    display: block;
    margin: 0;
  }

  .cdk-filters >>> .el-form-item__label {
    display: block;
    float: none;
    padding: 0 0 4px;
    font-size: 12px;
    line-height: 1.4;
    text-align: left;
  }

  .cdk-filters >>> .el-form-item__content {
    display: block;
    margin-left: 0;
    font-size: 13px;
    line-height: normal;
  }

  .cdk-filters >>> .el-form-item__content > * {
    width: 100%;
  }

  .cdk-filters .cdk-w-sm,
  .cdk-filters .cdk-w-pkg,
  .cdk-filters .cdk-w-md,
  .cdk-filters .cdk-w-date {
    width: 100%;
  }

  /* 日期 / 搜索 / 按钮跨两列 */
  .cdk-filters .el-form-item:nth-child(3),
  .cdk-filters .el-form-item:nth-child(4),
  .cdk-filters .el-form-item:nth-child(5) {
    grid-column: 1 / -1;
  }

  /* 搜索：类型 + 关键词 排一行 */
  .cdk-filters >>> .el-form-item:nth-child(4) .el-form-item__content {
    display: grid;
    grid-template-columns: 106px 1fr;
    gap: 8px;
  }

  /* 下拉默认最小宽度由内容撑开，会顶破网格列宽 */
  .cdk-filters >>> .el-form-item:nth-child(4) .el-form-item__content > * {
    min-width: 0;
  }

  .cdk-filters >>> .el-form-item:nth-child(5) .el-form-item__content {
    display: flex;
    gap: 10px;
  }

  .cdk-filters >>> .el-form-item:nth-child(5) .el-button {
    flex: 1;
  }

  .cdk-filters .el-form-item:nth-child(5) .el-button {
    margin-left: 0;
  }

  .cdk-actions {
    gap: 12px;
  }

  .cdk-actions .el-button {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/User2/BuyCdk.vue